import React from 'react';
import { SEVERITY_CLASSES } from '../types';
import Panel from '../../components/ui/Panel';
import { DataTable } from '../../components/table/DataTable';
import type { ColumnDef } from '@tanstack/react-table';
import { PANEL_BADGE } from './panelChrome';

interface ComparisonRow {
  metric: string;
  category: string;
  values: (string | number | null | any[])[];
  flag: string | null;
}

interface LogMeta {
  id: string;
  serial?: string;
  severity?: string;
}

interface ComparisonTableProps {
  logs: LogMeta[];
  comparison: ComparisonRow[];
  commonIssues?: string[];
  keyDifferences?: string[];
}

const FLAG_SEVERITY: Record<string, { sev: 'critical' | 'warning'; glyph: string; label: string }> = {
  large_spread: { sev: 'critical', glyph: '▲', label: 'Large spread' },
  notable_spread: { sev: 'warning', glyph: '△', label: 'Notable spread' },
};

const ComparisonTable: React.FC<ComparisonTableProps> = ({
  logs, comparison, commonIssues, keyDifferences,
}) => {
  if (!comparison || comparison.length === 0) return null;

  const hasIssues = !!commonIssues && commonIssues.length > 0;
  const hasDifferences = !!keyDifferences && keyDifferences.length > 0;

  const columns: ColumnDef<ComparisonRow, any>[] = [
    {
      id: 'metric',
      header: 'Metric',
      meta: {
        cellClass: 'whitespace-nowrap font-mono text-on-surface-variant',
        // The flagged metric wears a ring; the glyph and the hidden word beside it say the same.
        cellAttrs: (row) => {
          const flag = row.flag ? (FLAG_SEVERITY[row.flag] ?? FLAG_SEVERITY.notable_spread) : undefined;
          return flag ? { className: `ring-1 ring-inset ${SEVERITY_CLASSES[flag.sev].ring}` } : {};
        },
      },
      cell: ({ row }) => {
        const r = row.original;
        const flag = r.flag ? (FLAG_SEVERITY[r.flag] ?? FLAG_SEVERITY.notable_spread) : undefined;
        const flagClasses = flag ? SEVERITY_CLASSES[flag.sev] : null;
        return (
          <>
            {r.metric}
            {flag && flagClasses && (
              <span className={`ml-1.5 text-xs ${flagClasses.text}`}>
                <span aria-hidden="true">{flag.glyph}</span>
                <span className="sr-only">{flag.label}</span>
              </span>
            )}
          </>
        );
      },
    },
    ...logs.map((log, j): ColumnDef<ComparisonRow, any> => ({
      id: `log-${j}`,
      header: log.serial || log.id.slice(0, 8),
      meta: { align: 'right', numeric: true, cellClass: 'min-w-[5rem] font-mono text-on-surface', headerClass: 'min-w-[5rem] font-mono' },
      cell: ({ row }) => {
        const val = row.original.values[j];
        return val == null ? '—' : Array.isArray(val) ? val.join(', ') : String(val);
      },
    })),
  ];

  return (
    <Panel
      level={3}
      title="Comparison"
      actions={<span className={PANEL_BADGE}>{logs.length} flights</span>}
      padding="none"
      className="overflow-hidden"
    >
      {(hasIssues || hasDifferences) && (
        <div className="grid min-w-0 gap-3 p-3 sm:grid-cols-2">
          {hasIssues && (
            <div className="min-w-0">
              <h4 className="mb-1 text-xs font-semibold text-on-surface-variant">Common issues</h4>
              <ul className="flex flex-col gap-0.5">
                {commonIssues!.map((issue, i) => (
                  <li key={i} className={`break-words text-sm ${SEVERITY_CLASSES.warning.text}`}>{issue}</li>
                ))}
              </ul>
            </div>
          )}
          {hasDifferences && (
            <div className="min-w-0">
              <h4 className="mb-1 text-xs font-semibold text-on-surface-variant">Key differences</h4>
              <ul className="flex flex-col gap-0.5">
                {keyDifferences!.map((diff, i) => (
                  <li key={i} className="break-words text-sm text-on-surface">{diff}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <DataTable<ComparisonRow>
        data={comparison}
        columns={columns}
        getRowId={(_, i) => String(i)}
        on="panel"
        density="compact"
        striped
        pinFirstColumn
        empty={null}
      />
    </Panel>
  );
};

export default ComparisonTable;
